import dot from 'dot-prop-immutable';

import { UNITS, WEIGHT_UNITS } from 'constants/all';

import { LOCATION_CHANGE } from 'store/app/types';

import {
  getEncodedCADViewerState,
  decodeEncodedCADViewerState,
} from '../utils';
import { types } from './actions';

const DIMENSIONAL_PRECISION = 'partviewer:dimensionalPrecision';
const DISPLAY_UNITS = 'partviewer:displayUnits';

const initialState = () => ({
  activeBodies: [], // array of body viewer ids that drive interaction such as wireframe and dimensions
  activeGeometricPartFileUuids: [],
  caliperObjects: [], // an object is a single selected entity (faces, edges, vertices) for use in caliper tool, these are historical, drive historicalCaliperObjects in viewer-component state, historicalCaliperOptions are used for chat annotations
  caliperToolSelections: [], // caliper tool selections are the selected object as well as its viewer state at the moment of selection, drive caliperObjects in viewer-component state
  captureViewerState: false,
  capturedCaliperCADState: {},
  capturedCaliperState: {}, // state we capture every time a caliper measurement selection is made
  currentMeasurement: null, // float value of current measurement result
  dimensionalPrecision: +localStorage.getItem(DIMENSIONAL_PRECISION) || 3, // decimal precision of displayed measurements
  displayScale: 1.0, // drives the values shown in measurements, dimensions, and meta data relative to actual rendering entity sizes
  displayUnits: localStorage.getItem(DISPLAY_UNITS) ?? UNITS.IN,
  displayWeightUnits: WEIGHT_UNITS.LB,
  enabledBodies: [], // determines interactive state with assembly tree
  expandedBodies: [], // determines what values are expanded for the asssembly tree
  explodedState: {}, // object of assembly instance id to a float factor value to dictate how much to translate children
  featureSnapTo: null, // object of feature info being requested to be snapped to
  focusedBodies: [], // array of body viewer ids that drive zoomTo and visibility inversion logic
  focusedUuid: null, // determines what set of manufacturability feedback/features is being extracted
  forceFocus: false, // boolean value for overriding natural focusBodies changing behavior to trigger focus action
  forceView: null, // object that tells us when to perform visual snap to actions like zoom to fit and view normal to
  hasPMIAnnotations: false, //false until partviewer says there is PMI
  highlight: [], // highlight info from selected features and feedback
  hoverBody: null, // viewer id of currently hovered body in the assembly tree
  interrogationInfo: {},
  isDimensionToolActive: false,
  isExplodedViewToolActive: false,
  isFocusedViewActive: false,
  isGeometricInteractionReady: true,
  isHighlightViewActive: false,
  isMeasurementToolActive: false,
  isOptimalBoundingBoxToolActive: false,
  isPMIViewActive: false,
  isSelectorToolActive: false,
  isWireFrameToolActive: false,
  crossSectionEnabled: false,
  crossSectionInverted: false,
  crossSectionPickMode: false,
  crossSectionPosition: 0.5,
  crossSectionNormal: { x: 0, y: 0, z: -1 },
  materialStockResults: [],
  measurementComparison: null, // meta data around a measurement giving us angle and description of coincident relationship
  metaData: {},
  nativeUnits: UNITS.MM,
  prevFocusedBodies: [], // array for the sake of refocusing
  renderNativeColors: false,
  selectedBodies: [], // array of body viewer ids that drive navigational highlighting
  selectedFeatures: [], // a feature is defined by our backend interrogation, such as a hole or pocket, and consists of a collection of entities (faces, edges, vertices)
  selectedManufacturabilityFeedback: [],
  selectedObjects: [], // an object is a single selected entity (faces, edges, vertices), these are historical, drive historicalSelectedObjects in viewer-component state
  selectorCumulativeInfo: {}, // an object that tracks the cumulative data of selected objects (area, edge length)
  selectorToolSelections: [], // selector tools selections are the selected object as well as its viewer state at the moment of selection, drive selectedObjects in viewer-component state
  showAdditionalKeys: [], // array of keys for bodies tied to feature and feedback selections to render when selected
  showAdditionalKeysExplicit: [], // array of keys for bodies tied to feature and feedback selections to render when explicitly selected
  temporarilySimplified: new Set(), //body ids that are temporarily simplified in the part viewer
  temporarilySimplifiedParts: new Set(), //part ids of bodies that are temporarily simplified in the part viewer
  tooComplex: new Set(), //part ids that are too complex for the part viewer to render in full detail
  viewerPendingPartFileUuids: [], // array of part file uuids that are awaiting being loaded in the part viewer
  viewerReadyPartFileUuids: [], // array of part file uuids that have been loaded in the viewer and are available for interaction
  viewerState: null,
  showDxfControls: false,
  visibleBodies: [], // array of object ids, assemblies or bodies, that tell the viewer what to render
  visibleStockResultKeys: [], // array of top level object ids that describe renderings of stock options
});

//initialState is a factory so that state is reset everytime
const reducer = (state = initialState(), action) => {
  const { payload, type } = action;
  switch (type) {
    case LOCATION_CHANGE:
      return initialState();
    case types.CAD_VIEWER_APPEND_VISIBLE_BODIES:
      return {
        ...state,
        visibleBodies: Array.from(
          new Set([...state.visibleBodies, ...payload.visibleBodies]),
        ),
      };
    case types.CAD_VIEWER_RECEIVE_ACTIVE_BODIES:
      return {
        ...state,
        activeBodies: payload.activeBodies,
      };
    case types.CAD_VIEWER_RECEIVE_DISPLAY_SCALE:
      return {
        ...state,
        displayScale: payload.displayScale,
      };
    case types.CAD_VIEWER_RECEIVE_DISPLAY_UNITS: {
      localStorage.setItem(DISPLAY_UNITS, payload.displayUnits);
      return {
        ...state,
        displayUnits: payload.displayUnits,
      };
    }
    case types.CAD_VIEWER_RECEIVE_DIMENSIONAL_PRECISION: {
      localStorage.setItem(DIMENSIONAL_PRECISION, payload.dimensionalPrecision);
      return {
        ...state,
        dimensionalPrecision: payload.dimensionalPrecision,
      };
    }
    case types.CAD_VIEWER_RECEIVE_DISPLAY_WEIGHT_UNITS:
      return {
        ...state,
        displayWeightUnits: payload.displayWeightUnits,
      };
    case types.CAD_VIEWER_RECEIVE_ENABLED_BODIES:
      return {
        ...state,
        enabledBodies: payload.enabledBodies,
      };
    case types.CAD_VIEWER_RECEIVE_EXPANDED_BODIES:
      return {
        ...state,
        expandedBodies: payload.expandedBodies,
      };
    case types.FEATURE_SNAP_TO_RECEIVE:
      return {
        ...state,
        featureSnapTo: payload.feature,
      };
    case types.CAD_VIEWER_RECEIVE_FOCUSED_BODIES:
      return {
        ...state,
        prevFocusedBodies: state.focusedBodies.slice(),
        focusedBodies: payload.focusedBodies,
      };
    case types.CAD_VIEWER_RECEIVE_FOCUSED_UUID:
      return {
        ...state,
        focusedUuid: payload.focusedUuid,
      };
    case types.CAD_VIEWER_RECEIVE_FORCE_FOCUS:
      return {
        ...state,
        forceFocus: payload.forceFocus,
      };
    case types.CAD_VIEWER_RECEIVE_FORCE_VIEW:
      return {
        ...state,
        forceView: payload.forceView,
      };
    case types.CAD_VIEWER_RECEIVE_INTERROGATION_INFO:
      return {
        ...state,
        interrogationInfo: {
          documentHash: payload.documentHash,
          interrogationFileId: payload.interrogationFileId,
          task: payload.task,
        },
      };
    case types.CAD_VIEWER_RECEIVE_HIGHLIGHT:
      return {
        ...state,
        highlight: payload.highlight,
      };
    case types.CAD_VIEWER_RECEIVE_HOVER_BODY:
      return {
        ...state,
        hoverBody: payload.hoverBody,
      };
    case types.CAD_VIEWER_RECEIVE_META_DATA:
      return {
        ...state,
        metaData: payload.metaData,
      };
    case types.CAD_VIEWER_RECEIVE_NATIVE_UNITS:
      return {
        ...state,
        nativeUnits: payload.nativeUnits,
      };
    case types.CAD_VIEWER_RECEIVE_SELECTED_BODIES:
      return {
        ...state,
        selectedBodies: payload.selectedBodies,
      };
    case types.CAD_VIEWER_RECEIVE_SHOW_ADDITIONAL_KEYS:
      return {
        ...state,
        showAdditionalKeys: payload.showAdditionalKeys,
      };
    case types.CAD_VIEWER_RECEIVE_SHOW_ADDITIONAL_KEYS_EXPLICIT:
      return {
        ...state,
        showAdditionalKeysExplicit: payload.showAdditionalKeysExplicit,
      };
    case types.CAD_VIEWER_RECEIVE_TEMPORARILY_SIMPLIFIED:
      return {
        ...state,
        temporarilySimplified: payload.temporarilySimplified,
        temporarilySimplifiedParts: payload.temporarilySimplifiedParts,
      };
    case types.CAD_VIEWER_RECEIVE_TOO_COMPLEX:
      return {
        ...state,
        tooComplex: payload.tooComplex,
      };
    case types.CAD_VIEWER_RECEIVE_SHOW_DXF_CONTROLS: {
      const nextShowDxf = payload.showDxfControls;
      return {
        ...state,
        showDxfControls: nextShowDxf,
        ...(nextShowDxf
          ? {
              crossSectionEnabled: false,
              crossSectionPickMode: false,
              isWireFrameToolActive: false,
              isHighlightViewActive: false,
              isFocusedViewActive: false,
            }
          : {}),
      };
    }
    case types.CAD_VIEWER_RECEIVE_VISIBLE_BODIES:
      return {
        ...state,
        visibleBodies: payload.visibleBodies,
      };
    case types.CAD_VIEWER_REMOVE_VISIBLE_BODIES:
      return {
        ...state,
        visibleBodies: state.visibleBodies.filter(
          (viewerId) => !payload.viewerIds.includes(viewerId),
        ),
      };
    case types.CAD_VIEWER_SET_GEOMETRIC_ENTITIES:
      return {
        ...state,
        selectedFeatures: [],
        selectedManufacturabilityFeedback: [],
        showAdditionalKeys: [],
        showAdditionalKeysExplicit: payload.showAdditionalKeysExplicit || [],
        highlight: [],
      };
    case types.CAD_VIEWER_SET_RENDER_NATIVE_COLORS:
      return {
        ...state,
        renderNativeColors: payload.renderNativeColors,
      };
    case types.CAD_VIEWER_UPDATE_EXISTING_PART:
      return {
        ...state,
        processTag: payload.processTag,
      };
    case types.DIMENSION_TOOL_TOGGLE: {
      const nextDimensionsToolState = !state.isDimensionToolActive;
      const nextExplodedToolState = nextDimensionsToolState
        ? false
        : state.isExplodedViewToolActive;
      const nextExplodedState = nextDimensionsToolState
        ? {}
        : state.explodedState;
      return {
        ...state,
        explodedState: nextExplodedState,
        isDimensionToolActive: nextDimensionsToolState,
        isExplodedViewToolActive: nextExplodedToolState,
        isOptimalBoundingBoxToolActive: false,
      };
    }
    case types.CROSS_SECTION_SET_ENABLED: {
      const nextCrossSectionEnabled = payload.enabled;
      return {
        ...state,
        ...(nextCrossSectionEnabled
          ? {
              explodedState: {},
              isExplodedViewToolActive: false,
            }
          : {}),
        crossSectionEnabled: nextCrossSectionEnabled,
        crossSectionPickMode: nextCrossSectionEnabled
          ? state.crossSectionPickMode
          : false,
        ...(nextCrossSectionEnabled
          ? {
              crossSectionPosition: 0.5,
              crossSectionNormal: { x: 0, y: 0, z: -1 },
              crossSectionInverted: false,
            }
          : {}),
      };
    }
    case types.CROSS_SECTION_SET_POSITION:
      return {
        ...state,
        crossSectionPosition: Math.min(
          1,
          Math.max(0, Number(payload.position)),
        ),
      };
    case types.CROSS_SECTION_TOGGLE_INVERTED:
      return {
        ...state,
        crossSectionInverted: !state.crossSectionInverted,
      };
    case types.CROSS_SECTION_SET_PICK_MODE: {
      // Face picking requires shaded faces; wireframe hides them.
      const nextPick = !!payload.pickMode && !state.isWireFrameToolActive;
      return {
        ...state,
        crossSectionPickMode: nextPick,
        ...(nextPick
          ? {
              isSelectorToolActive: false,
              isMeasurementToolActive: false,
            }
          : {}),
      };
    }
    case types.CROSS_SECTION_SET_NORMAL:
      return {
        ...state,
        crossSectionNormal: payload.normal,
        ...(payload.position != null &&
        Number.isFinite(Number(payload.position))
          ? {
              crossSectionPosition: Math.min(
                1,
                Math.max(0, Number(payload.position)),
              ),
            }
          : {}),
      };
    case types.OPTIMAL_BOUNDING_BOX_TOOL_TOGGLE: {
      const nextOptimalBoxToolState = !state.isOptimalBoundingBoxToolActive;
      const nextExplodedToolState = nextOptimalBoxToolState
        ? false
        : state.isExplodedViewToolActive;
      const nextExplodedState = nextOptimalBoxToolState
        ? {}
        : state.explodedState;
      return {
        ...state,
        explodedState: nextExplodedState,
        isOptimalBoundingBoxToolActive: nextOptimalBoxToolState,
        isDimensionToolActive: false,
        isExplodedViewToolActive: nextExplodedToolState,
      };
    }
    case types.EXPLODED_VIEW_TOOL_DISABLE:
      return {
        ...state,
        isExplodedViewToolActive: false,
        explodedState: {},
      };
    case types.EXPLODED_VIEW_TOOL_RECEIVE_EXPLODED_STATE:
      return dot.set(
        state,
        `explodedState.${payload.assemblyInstanceId}`,
        payload.factor,
      );
    case types.EXPLODED_VIEW_TOOL_TOGGLE: {
      const nextExplodedToolState = !state.isExplodedViewToolActive;
      const nextDimensionsToolState = nextExplodedToolState
        ? false
        : state.isDimensionToolActive;
      const nextOptimalBoxToolState = nextExplodedToolState
        ? false
        : state.isOptimalBoundingBoxToolActive;
      return {
        ...state,
        isExplodedViewToolActive: nextExplodedToolState,
        isDimensionToolActive: nextDimensionsToolState,
        isOptimalBoundingBoxToolActive: nextOptimalBoxToolState,
        ...(nextExplodedToolState
          ? {
              crossSectionEnabled: false,
              crossSectionPickMode: false,
              crossSectionPosition: 0.5,
              crossSectionNormal: { x: 0, y: 0, z: -1 },
              crossSectionInverted: false,
            }
          : {}),
      };
    }
    case types.FEATURE_SELECTION_RECEIVE:
      return {
        ...state,
        selectedFeatures: payload.features,
      };
    case types.FOCUSED_VIEW_TOGGLE:
      return {
        ...state,
        isFocusedViewActive: !state.isFocusedViewActive,
      };
    case types.GEOMETRIC_PART_FILE_BULK_PREPARE: {
      const newActiveGeometricPartFileUuids = payload.partFileUuids.filter(
        (partFileUuid) =>
          !state.activeGeometricPartFileUuids.includes(partFileUuid),
      );
      return !newActiveGeometricPartFileUuids.length
        ? state
        : {
            ...state,
            activeGeometricPartFileUuids: [
              ...state.activeGeometricPartFileUuids,
              ...newActiveGeometricPartFileUuids,
            ],
          };
    }
    case types.HIGHLIGHT_VIEW_TOGGLE:
      return {
        ...state,
        isHighlightViewActive: !state.isHighlightViewActive,
        isWireFrameToolActive: !state.isHighlightViewActive
          ? false
          : state.isWireFrameToolActive,
      };
    case types.MANUFACTURABILITY_FEEDBACK_SELECTION_RECEIVE:
      return {
        ...state,
        selectedManufacturabilityFeedback: payload.manufacturabilityFeedback,
      };
    case types.MATERIAL_STOCK_RECEIVE_NEW_RESULT:
      return {
        ...state,
        materialStockResults: state.materialStockResults.concat([
          payload.result,
        ]),
      };
    case types.MATERIAL_STOCK_RECEIVE_VISIBLE_KEYS:
      return {
        ...state,
        visibleStockResultKeys: payload.resultKeys,
      };
    case types.MEASUREMENT_TOOL_TOGGLE: {
      // measurement tool and selector tool cannot both be active at the same time
      const nextMeasurementToolState = !state.isMeasurementToolActive;
      const nextSelectorToolState = nextMeasurementToolState
        ? false
        : state.isSelectorToolActive;

      return {
        ...state,
        isMeasurementToolActive: nextMeasurementToolState,
        isSelectorToolActive: nextSelectorToolState,
        crossSectionPickMode: false,
        selectorCumulativeInfo: {},
        selectorToolSelections: [],
        selectedObjects: [],
        caliperObjects: [],
        caliperToolSelections: [],
        currentMeasurement: null,
        measurementComparison: null,
      };
    }
    case types.MEASUREMENT_TOOL_DISABLE:
      return {
        ...state,
        isMeasurementToolActive: false,
        currentMeasurement: null,
        caliperToolSelections: [],
        measurementComparison: null,
      };
    case types.MEASUREMENT_TOOL_ENABLE:
      // measurement tool and selector tool cannot both be active at the same time
      // clear measurement tool when enabling when previously disabled
      return {
        ...state,
        isMeasurementToolActive: true,
        isSelectorToolActive: false,
        crossSectionPickMode: false,
        caliperObjects: state.isMeasurementToolActive
          ? state.caliperObjects
          : [],
        caliperToolSelections: state.isMeasurementToolActive
          ? state.caliperToolSelections
          : [],
        currentMeasurement: state.isMeasurementToolActive
          ? state.currentMeasurement
          : null,
      };
    case types.MEASUREMENT_TOOL_RECEIVE_COMPARISON:
      return {
        ...state,
        measurementComparison: payload.comparison,
      };
    case types.MEASUREMENT_TOOL_RECEIVE_SELECTIONS:
      return {
        ...state,
        capturedCaliperState: payload.viewerState,
        capturedCaliperCADState: getEncodedCADViewerState(state),
        caliperToolSelections: payload.selections,
        currentMeasurement: null,
        measurementComparison: null,
      };
    case types.MEASUREMENT_TOOL_REMOVE_SELECTIONS:
      return {
        ...state,
        capturedCaliperState: {},
        capturedCaliperCADState: {},
        caliperToolSelections: [],
        currentMeasurement: null,
        measurementComparison: null,
      };
    case types.MEASUREMENT_TOOL_RECEIVE_MEASUREMENT:
      return {
        ...state,
        currentMeasurement: payload.distance,
      };
    case types.MEASUREMENT_OBJECTS_MANAGER_RECEIVE_SELECTIONS:
      return {
        ...state,
        caliperObjects: payload.caliperObjects,
        measurementComparison: null,
      };
    case types.MEASUREMENT_OBJECTS_MANAGER_REMOVE_SELECTIONS:
      return {
        ...state,
        caliperObjects: [],
        measurementComparison: null,
      };
    case types.PMI_VIEW_HIDE:
      return {
        ...state,
        isPMIViewActive: false,
      };
    case types.PMI_VIEW_SHOW:
      return {
        ...state,
        isPMIViewActive: state.hasPMIAnnotations,
      };
    case types.PMI_VIEW_TOGGLE:
      return {
        ...state,
        isPMIViewActive: state.hasPMIAnnotations && !state.isPMIViewActive,
      };
    case types.CAD_VIEWER_RECEIVE_HAS_PMI_ANNOTATIONS:
      return {
        ...state,
        hasPMIAnnotations: payload.hasPMIAnnotations,
      };
    case types.RENDER_NATIVE_COLORS_TOGGLE:
      return {
        ...state,
        renderNativeColors: !state.renderNativeColors,
      };
    case types.SELECTED_OBJECTS_MANAGER_RECEIVE_SELECTIONS:
      return {
        ...state,
        selectedObjects: state.selectedObjects.concat(payload.selections),
      };
    case types.SELECTED_OBJECTS_MANAGER_REMOVE_ALL_SELECTIONS:
      return {
        ...state,
        selectedObjects: [],
      };
    case types.SELECTED_OBJECTS_MANAGER_REMOVE_SELECTIONS: {
      let { selectedObjects } = state;
      payload.selections.forEach((deselected) => {
        const isStep = deselected.obj.includes('|');
        if (isStep) {
          selectedObjects = selectedObjects.filter(
            (selectedObject) => selectedObject.obj !== deselected.obj,
          );
        } else {
          // stls
          selectedObjects = selectedObjects.filter(
            (selectedObject) => selectedObject.f !== deselected.f,
          );
        }
      });
      return {
        ...state,
        selectedObjects: [...selectedObjects],
      };
    }
    case types.SELECTOR_TOOL_BOX_SELECT:
      return {
        ...state,
        selectorToolSelections: payload.selections,
      };
    case types.SELECTOR_TOOL_CLEAR_ALL_SELECTIONS:
      return {
        ...state,
        selectorToolSelections: [],
      };
    case types.SELECTOR_TOOL_DISABLE:
      return {
        ...state,
        isSelectorToolActive: false,
        selectorToolSelections: [],
        selectorCumulativeInfo: {},
      };
    case types.SELECTOR_TOOL_ENABLE: {
      // clear selections when enabling the selector tool
      const nextSelectorToolSelections = state.isSelectorToolActive
        ? state.selectorToolSelections
        : [];
      return {
        ...state,
        isMeasurementToolActive: false,
        isSelectorToolActive: true,
        crossSectionPickMode: false,
        selectedObjects: [],
        selectorToolSelections: nextSelectorToolSelections,
        selectorCumulativeInfo: {},
      };
    }
    case types.SELECTOR_TOOL_RECEIVE_CUMULATIVE_INFO:
      return {
        ...state,
        selectorCumulativeInfo: payload.cumulativeInfo,
      };
    case types.SELECTOR_TOOL_RECEIVE_SELECTION:
      return {
        ...state,
        selectorToolSelections: state.selectorToolSelections.concat([
          {
            ...payload.selection,
            cadViewerState: getEncodedCADViewerState(state),
          },
        ]),
      };
    case types.SELECTOR_TOOL_REMOVE_SELECTION: {
      const isStep = payload.obj.includes('|');
      let selectorToolSelections = [];
      if (isStep) {
        selectorToolSelections = state.selectorToolSelections.filter(
          ({ selectedObject }) => selectedObject.obj !== payload.obj,
        );
      } else {
        // stls
        selectorToolSelections = state.selectorToolSelections.filter(
          ({ selectedObject }) => selectedObject.f !== payload.f,
        );
      }
      return {
        ...state,
        selectorToolSelections,
      };
    }
    case types.SELECTOR_TOOL_TOGGLE: {
      // measurement tool and selector tool cannot both be active at the same time
      const nextSelectorToolState = !state.isSelectorToolActive;
      const nextMeasurementToolState = nextSelectorToolState
        ? false
        : state.isMeasurementToolActive;
      const nextSelectedObjects = nextSelectorToolState
        ? []
        : state.selectedObjects;

      return {
        ...state,
        isMeasurementToolActive: nextMeasurementToolState,
        isSelectorToolActive: nextSelectorToolState,
        crossSectionPickMode: false,
        selectedObjects: nextSelectedObjects,
        selectorToolSelections: [],
        caliperObjects: [],
        caliperToolSelections: [],
        currentMeasurement: null,
        measurementComparison: null,
        selectorCumulativeInfo: {},
      };
    }
    case types.SET_GEOMETRIC_INTERACTION_READY:
      return {
        ...state,
        isGeometricInteractionReady: payload.isReady,
      };
    case types.VIEWER_PART_FILE_QUEUE_LOAD_SUCCESS:
      return {
        ...state,
        viewerPendingPartFileUuids: state.viewerPendingPartFileUuids.includes(
          payload.partFileUuid,
        )
          ? state.viewerPendingPartFileUuids.filter(
              (partFileUuid) => partFileUuid !== payload.partFileUuid,
            )
          : state.viewerPendingPartFileUuids,
        viewerReadyPartFileUuids: state.viewerReadyPartFileUuids.includes(
          payload.partFileUuid,
        )
          ? state.viewerReadyPartFileUuids
          : [...state.viewerReadyPartFileUuids, payload.partFileUuid],
      };
    case types.VIEWER_PART_FILE_QUEUE_PENDING_ADD_TO_END:
      return state.viewerPendingPartFileUuids.includes(payload.partFileUuid)
        ? state
        : {
            ...state,
            viewerPendingPartFileUuids: [
              ...state.viewerPendingPartFileUuids,
              payload.partFileUuid,
            ],
          };
    case types.VIEWER_PART_FILE_QUEUE_PENDING_ADD_TO_START:
      return state.viewerPendingPartFileUuids.includes(payload.partFileUuid)
        ? state
        : {
            ...state,
            viewerPendingPartFileUuids: [
              payload.partFileUuid,
              ...state.viewerPendingPartFileUuids,
            ],
          };
    case types.VIEWER_STATE_CAPTURE_NEXT_STATE_REQUEST:
      return {
        ...state,
        captureViewerState: true,
      };
    case types.VIEWER_STATE_RECEIVE:
      return {
        ...state,
        captureViewerState: false,
      };
    case types.VIEWER_STATE_SNAP_TO:
      return {
        ...state,
        ...decodeEncodedCADViewerState(payload.cadViewerState || {}),
        viewerState: payload.viewerState,
      };
    case types.VIEWER_STATE_RESET:
      return {
        ...state,
        viewerState: null,
      };
    case types.WIRE_FRAME_TOOL_TOGGLE: {
      const nextWireFrame = !state.isWireFrameToolActive;
      return {
        ...state,
        isWireFrameToolActive: nextWireFrame,
        isHighlightViewActive: nextWireFrame
          ? false
          : state.isHighlightViewActive,
        crossSectionPickMode: nextWireFrame
          ? false
          : state.crossSectionPickMode,
      };
    }
    default:
      return state;
  }
};

const getScope = (state) => state.modules.common.viewer.cadViewer.cadViewer;

export const getInterrogationInfo = (state) =>
  getScope(state).interrogationInfo;

export const getIsDimensionToolActive = (state) =>
  getScope(state).isDimensionToolActive;

export const getCrossSectionEnabled = (state) =>
  getScope(state).crossSectionEnabled;

export const getCrossSectionInverted = (state) =>
  getScope(state).crossSectionInverted;

export const getCrossSectionPickMode = (state) =>
  getScope(state).crossSectionPickMode;

export const getCrossSectionPosition = (state) =>
  getScope(state).crossSectionPosition;

export const getCrossSectionNormal = (state) =>
  getScope(state).crossSectionNormal;

export const getIsMeasurementToolActive = (state) =>
  getScope(state).isMeasurementToolActive;

export const getIsOptimalBoundingBoxToolActive = (state) =>
  getScope(state).isOptimalBoundingBoxToolActive;

export const getIsSelectorToolActive = (state) =>
  getScope(state).isSelectorToolActive;

export const getIsWireFrameToolActive = (state) =>
  getScope(state).isWireFrameToolActive;

export const getRenderNativeColors = (state) =>
  getScope(state).renderNativeColors;

export const getIsHighlightViewActive = (state) =>
  getScope(state).isHighlightViewActive;

export const getIsPMIViewActive = (state) => {
  const { isPMIViewActive, hasPMIAnnotations } = getScope(state);
  return hasPMIAnnotations && isPMIViewActive;
};

export const getHasPMIAnnotations = (state) =>
  getScope(state).hasPMIAnnotations;

export const getIsFocusedViewActive = (state) =>
  getScope(state).isFocusedViewActive;

export const getIsExplodedViewActive = (state) =>
  getScope(state).isExplodedViewToolActive;

export const getExplodedState = (state) => getScope(state).explodedState;

export const getExplodedFactorForViewerId = (state, viewerId) =>
  getScope(state).explodedState[viewerId];

export const getHighlight = (state) => getScope(state).highlight;

export const getMetaData = (state) => getScope(state).metaData;

export const getMeasurementComparison = (state) =>
  getScope(state).measurementComparison;

export const getNativeUnits = (state) => getScope(state).nativeUnits;

export const getDisplayUnits = (state) => getScope(state).displayUnits;

export const getDimensionalPrecision = (state) =>
  getScope(state).dimensionalPrecision;

export const getDisplayWeightUnits = (state) =>
  getScope(state).displayWeightUnits;

export const getDisplayScale = (state) => getScope(state).displayScale;

export const getSelectedFeatures = (state) => getScope(state).selectedFeatures;

export const getSelectedManufacturabilityFeedback = (state) =>
  getScope(state).selectedManufacturabilityFeedback;

export const getSelectedObjects = (state) => getScope(state).selectedObjects;

export const getSelectorCumulativeInfo = (state) =>
  getScope(state).selectorCumulativeInfo;

export const getSelectorToolSelections = (state) =>
  getScope(state).selectorToolSelections;

export const getAreSelectorToolSelectionsPresent = (state) =>
  getScope(state).selectorToolSelections.length > 0;

export const getCaliperObjects = (state) => getScope(state).caliperObjects;

export const getCaliperToolSelections = (state) =>
  getScope(state).caliperToolSelections;

export const getAreCaliperToolSelectionsPresent = (state) =>
  getScope(state).caliperToolSelections.length > 0;

export const getCurrentMeasurementValue = (state) =>
  getScope(state).currentMeasurement;

export const getCapturedCaliperState = (state) =>
  getScope(state).capturedCaliperState;

export const getCapturedCaliperCADState = (state) =>
  getScope(state).capturedCaliperCADState;

export const getViewerState = (state) => getScope(state).viewerState;

export const shouldCaptureViewerState = (state) =>
  getScope(state).captureViewerState;

export const getActiveBodies = (state) => getScope(state).activeBodies;

export const getActiveGeometricPartFileUuids = (state) =>
  getScope(state).activeGeometricPartFileUuids;

export const getSelectedBodies = (state) => getScope(state).selectedBodies;

export const getFocusedBodies = (state) => getScope(state).focusedBodies;

export const getIsBodyFocused = (state, viewerId) =>
  getFocusedBodies(state).includes(viewerId);

export const getPrevFocusedBodies = (state) =>
  getScope(state).prevFocusedBodies;

export const getShowDxfControls = (state) => getScope(state).showDxfControls;

export const getVisibleBodies = (state) => getScope(state).visibleBodies;

export const getTemporarilySimplified = (state) =>
  getScope(state).temporarilySimplified;

export const getTemporarilySimplifiedParts = (state) =>
  getScope(state).temporarilySimplifiedParts;

export const getTooComplexForPartViewer = (state) => getScope(state).tooComplex;

export const getExpandedBodies = (state) => getScope(state).expandedBodies;

export const getEnabledBodies = (state) => getScope(state).enabledBodies;

export const getIsBodyEnabled = (state, viewerId) =>
  getExpandedBodies(state).includes(viewerId);

export const getHoverBody = (state) => getScope(state).hoverBody;

// TODO: REFACTOR TO getFocusedPartFileUuid
export const getFocusedUuid = (state) => getScope(state).focusedUuid;

export const getForceFocus = (state) => getScope(state).forceFocus;

export const getForceView = (state) => getScope(state).forceView;

export const getFeatureSnapTo = (state) => getScope(state).featureSnapTo;

export const getShowAdditionalKeys = (state) =>
  getScope(state).showAdditionalKeys;

export const getShowAdditionalKeysExplicit = (state) =>
  getScope(state).showAdditionalKeysExplicit;

export const getIsInteractionReady = (state) =>
  getScope(state).isGeometricInteractionReady;

export const getEncodedCADState = (state) => {
  return getEncodedCADViewerState(getScope(state));
};

export const getMaterialStockResults = (state) =>
  getScope(state).materialStockResults;

export const getVisibleStockResultKeys = (state) =>
  getScope(state).visibleStockResultKeys;

export const getViewerPendingPartFileUuids = (state) =>
  getScope(state).viewerPendingPartFileUuids;

export const getIsViewerReadyForPartFile = (state, partFileUuid) =>
  getScope(state).viewerReadyPartFileUuids.includes(partFileUuid);

export default reducer;
